import { BaseUserProfile, EnabledFeatures, Gym, IEnabledFeatures, ProgramType } from 'fitbeat.models';
import { Level, UserProfile } from '../../entities';

export interface ISignState {
  longTermGoal: ProgramType;
  skillLevel: Level;
  currentScreenIndex: number;
}

export enum SignupFlowType {
  TypeOne = 'TypeOne',
}

export enum SignupScreen {
  Start = 'Start',
  IAPSubscription = 'IAPSubscription',
  FitnessGoal = 'FitnessGoal',
  SkillLevel = 'SkillLevel',
  StrengthLevel = 'StrengthLevel',
  ConnectedAppEntryPoint = 'ConnectedAppEntryPoint',
  Gender = 'Gender',
  Nutrition = 'Nutrition',
  DateOfBirth = 'DateOfBirth',
  Height = 'Height',
  Weight = 'Weight',
  NotificationPermission = 'NotificationPermission',
  DashboardTabNavigator = 'DashboardTabNavigator',
  UserNames = 'UserNames',
  Nickname = 'Nickname',
  CaptureAvatar = 'CaptureAvatar',
  Allergens = 'Allergens',
}

type SignupFlowScreens = { [key in SignupFlowType]: SignupScreen[] };

const SignupFlows: SignupFlowScreens = {
  [SignupFlowType.TypeOne]: [
    SignupScreen.Start,
    SignupScreen.IAPSubscription,
    SignupScreen.UserNames,
    SignupScreen.Nickname,
    SignupScreen.CaptureAvatar,
    SignupScreen.FitnessGoal,
    SignupScreen.SkillLevel,
    SignupScreen.Gender,
    SignupScreen.StrengthLevel,
    SignupScreen.ConnectedAppEntryPoint,
    SignupScreen.Nutrition,
    SignupScreen.Allergens,
    SignupScreen.DateOfBirth,
    SignupScreen.Weight,
    SignupScreen.Height,
    SignupScreen.NotificationPermission,
  ],
};

export interface ISignupScreenWithIndex {
  screen: SignupScreen;
  index: number;
}

export interface INavigateToNextStepOptions {
  openInstantWorkout?: boolean;
  isFirstScreenOnStack?: boolean;
  userHasAllergies?: boolean;
}

export class SignupFlow {
  public static getScreenForSignupFlowType(signupFlowType: SignupFlowType): SignupScreen[] {
    return SignupFlows[signupFlowType];
  }

  public static getNextScreen(
    userProfile: UserProfile,
    currentScreen: SignupScreen,
    signupScreens: SignupScreen[],
    enabledFeatures: IEnabledFeatures,
    gym: Gym | null,
    skipRestOfFlow?: boolean,
    options?: INavigateToNextStepOptions,
  ): ISignupScreenWithIndex {

    const incrementScreen = (cs: SignupScreen): ISignupScreenWithIndex => {
      const index = signupScreens.findIndex((s) => s === cs);
      const screen = signupScreens[index + 1];

      return { index, screen };
    };
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);

    let nextScreenInfo = incrementScreen(currentScreen);

    if (nextScreenInfo.screen === SignupScreen.IAPSubscription && BaseUserProfile.hasActivePlan(userProfile)) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    if (nextScreenInfo.screen === SignupScreen.IAPSubscription && hasAccessToAStudioGym) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    // TODO: The influencer signup flow will only work for gyms where IAP are configured
    if (nextScreenInfo.screen === SignupScreen.IAPSubscription && gym && !gym.shouldUseInAppPurchaseForMembership) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    const userHasBothNames = userProfile.firstname && userProfile.lastname;
    if (nextScreenInfo.screen === SignupScreen.UserNames && userHasBothNames) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    const shouldNotRequestNickName = !hasAccessToAStudioGym || userProfile.nickname;
    if (nextScreenInfo.screen === SignupScreen.Nickname && shouldNotRequestNickName) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    const shouldShowCaptureAvatarStep = hasAccessToAStudioGym;
    if (nextScreenInfo.screen === SignupScreen.CaptureAvatar && !shouldShowCaptureAvatarStep) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    if (nextScreenInfo.screen === SignupScreen.Allergens && !options?.userHasAllergies) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    if (nextScreenInfo.screen === SignupScreen.DateOfBirth && userProfile.dateOfBirth) {
      nextScreenInfo = incrementScreen(nextScreenInfo.screen);
    }

    return {
      index: nextScreenInfo.index,
      screen: (nextScreenInfo.screen && !skipRestOfFlow) ? nextScreenInfo.screen : SignupScreen.DashboardTabNavigator,
    };
  }
}
